import { useCallback } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { ActionIcon, Flex, TextInput, Tooltip } from "@mantine/core";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";

import { useQuestionnaireFormContext } from "../formContext";

export interface OptionProps {
    questionIndex: number;
    index: number;
    hideRemoveButton?: boolean;
}

export const Option = ({ questionIndex, index, hideRemoveButton }: OptionProps) => {
    const { t } = useTranslation(["common"]);

    const questionnaireForm = useQuestionnaireFormContext();

    const handleRemove = useCallback(() => {
        questionnaireForm.removeListItem(`questions.${questionIndex}.options`, index);
    }, [questionnaireForm, questionIndex, index]);

    return (
        <Draggable
            key={`${questionnaireForm.values?.questions[questionIndex].options[index].key}`}
            draggableId={`${questionnaireForm.values?.questions[questionIndex].options[index].key}`}
            index={index}
        >
            {(provided) => (
                <Flex
                    {...provided.draggableProps}
                    ref={provided.innerRef}
                    style={{ ...provided.draggableProps?.style }}
                    mb="sm"
                >
                    <Tooltip label={t("questionnaire_question_option_reorder")} withArrow>
                        <ActionIcon {...provided.dragHandleProps} mr="xs" variant="subtle" color="blue.400">
                            <IconGripVertical size="1em" />
                        </ActionIcon>
                    </Tooltip>
                    {!hideRemoveButton && (
                        <Tooltip label={t("questionnaire_question_option_remove")} withArrow>
                            <ActionIcon mr="xs" variant="subtle" color="red" onClick={handleRemove}>
                                <IconTrash size="1em" />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    <TextInput
                        {...questionnaireForm.getInputProps(`questions.${questionIndex}.options.${index}.text`)}
                        placeholder={t("questionnaire_question_option_value")}
                        style={{ flex: 1 }}
                    />
                </Flex>
            )}
        </Draggable>
    );
};
